import {Component, inject, Input} from '@angular/core';
import {DxTabsComponent} from "devextreme-angular";
import {ActivatedRoute, Router} from "@angular/router";
import {VacationsComponent} from "../vacations/vacations.component";
import {FloaterDaysComponent} from "../floater-days/floater-days.component";
import {LieuDaysComponent} from "../lieu-days/lieu-days.component";
import {AbsencesComponent} from "../absences/absences.component";
import {PersonalTimesComponent} from "../personal-times/personal-times.component";
import {AgencyTimeOffsComponent} from "../agency-time-offs/agency-time-offs.component";
import {AccessType, AuthService, PermissionType} from "@services";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type Tab = {
    type: TabType;
    text: string;
}

enum TabType {
    Vacation = 'Vacation',
    LieuDay = 'LieuDay',
    PersonalTime = 'PersonalTime',
    FloaterDay = 'FloaterDay',
    LeaveOfAbsence = 'LeaveOfAbsence',
    AgencyTimeOff = 'AgencyTimeOff'
}

@Component({
    selector: 'app-time-off-tabs',
    imports: [DxTabsComponent, VacationsComponent, FloaterDaysComponent, LieuDaysComponent, AbsencesComponent, PersonalTimesComponent, AgencyTimeOffsComponent],
    templateUrl: './time-off-tabs.component.html'
})
export class TimeOffTabsComponent {
    protected auth = inject(AuthService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);

    @Input() employeeId: string;
    @Input() offset: number = 136;
    @Input() embedded: boolean = false;

    private readonly allTabs = [
        {type: TabType.Vacation, text: 'Vacation', permission: PermissionType.VacationRequests, slug: 'vacation'},
        {type: TabType.LieuDay, text: 'Lieu Day', permission: PermissionType.LieuDayRequests, slug: 'lieu-day'},
        {type: TabType.PersonalTime, text: 'Personal Time', permission: PermissionType.PersonalTimeRequests, slug: 'personal-time'},
        {type: TabType.FloaterDay, text: 'Floater Day', permission: PermissionType.FloaterDayRequests, slug: 'floater-day'},
        {type: TabType.LeaveOfAbsence, text: 'Leave of Absence', permission: PermissionType.LeaveOfAbsenceRequests, slug: 'absence'},
        {type: TabType.AgencyTimeOff, text: 'Agency Time Off', permission: PermissionType.AgencyTimeOffRequests, slug: 'agency-time-off'},
    ];

    tabs: Tab[] = this.allTabs
        .filter(t => this.auth.checkPermission(t.permission, AccessType.Edit))
        .map(t => ({type: t.type, text: t.text}));

    selectedTab: TabType | null = null;
    protected TabType = TabType;

    constructor() {
        this.route.params.pipe(takeUntilDestroyed()).subscribe(params => {
            const tabParam = params['tab'];
            const foundTab = this.allTabs.find(t => t.slug === tabParam);

            if (foundTab && this.tabs.some(t => t.type === foundTab.type)) {
                this.selectedTab = foundTab.type;
            } else if (this.tabs.length > 0) {
                this.selectedTab = this.tabs[0].type;
            }
        });
    }

    protected async onTabClick(type: TabType) {
        this.selectedTab = type;
        const foundTab = this.allTabs.find(t => t.type === type);
        if (foundTab && !this.employeeId) {
            await this.router.navigate(['/time-off', foundTab.slug]);
        }
    }

    protected readonly AccessType = AccessType;
    protected readonly PermissionType = PermissionType;
}
